import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Button } from "components/ui/button";
import { ChevronLeft, Check, X } from "lucide-react";
import clsx from "clsx";

// Sample 3-level location data
const locations = [
  {
    name: "Phnom Penh",
    districts: [
      {
        name: "Chamkar Mon",
        communes: ["Olympic", "Tumnob Tuek"],
      },
      {
        name: "Boeng Keng Kang",
        communes: ["BKK Muoy", "BKK Pir", "BKK Bei"],
      },
    ],
  },
  {
    name: "Siem Reap",
    districts: [
      {
        name: "Svay Dangkum",
        communes: ["Commune 1", "Commune 2"],
      },
    ],
  },
];

export default function LocationPopover() {
  const [view, setView] = useState<"province" | "district" | "commune">("province");
  const [isOpen, setIsOpen] = useState(false);

  // Final selection
  const [selectedProvince, setSelectedProvince] = useState<string | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);
  const [selectedCommune, setSelectedCommune] = useState<string | null>(null);

  // Temporary navigation state
  const [activeProvince, setActiveProvince] = useState<string | null>(null);
  const [activeDistrict, setActiveDistrict] = useState<string | null>(null);

  const activeProvinceObj = locations.find((p) => p.name === activeProvince);
  const activeDistrictObj = activeProvinceObj?.districts.find((d) => d.name === activeDistrict);

  const getDisplayLabel = () => {
    return selectedCommune || selectedDistrict || selectedProvince || "All Location";
  };

  const handleFinalSelect = ({ province, district = null, commune = null }: { province: string; district?: string | null; commune?: string | null }) => {
    setSelectedProvince(province);
    setSelectedDistrict(district);
    setSelectedCommune(commune);
    setIsOpen(false);
  };

  const hasCheck = {
    province: (name: string) => selectedProvince === name,
    district: (name: string) => selectedDistrict === name,
    commune: (name: string) => selectedCommune === name,
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedProvince(null);
    setSelectedDistrict(null);
    setSelectedCommune(null);
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (open) {
          setView("province");
          setActiveProvince(null);
          setActiveDistrict(null);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          📍 {getDisplayLabel()}
          {selectedProvince && <X size={14} className="ml-1 text-muted-foreground hover:text-destructive" onClick={handleClear} />}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-72 p-0 max-h-[400px] overflow-y-auto">
        {/* Province View */}
        {view === "province" && (
          <div>
            <div className="p-3 bg-gray-100 border-b font-semibold">Location</div>
            {locations.map((prov) => (
              <div
                key={prov.name}
                onClick={() => {
                  setActiveProvince(prov.name);
                  setView("district");
                }}
                className="px-4 py-2 cursor-pointer hover:bg-muted flex justify-between items-center"
              >
                {prov.name}
                {hasCheck.province(prov.name) && <Check className="w-4 h-4 text-blue-600" />}
              </div>
            ))}
          </div>
        )}

        {/* District View */}
        {view === "district" && activeProvinceObj && (
          <div>
            <div className="flex items-center p-3 bg-gray-100 border-b font-semibold">
              <button
                onClick={() => {
                  setView("province");
                  setActiveProvince(null);
                }}
                className="mr-2 p-1 hover:bg-gray-200 rounded"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              Location
            </div>
            <div className="px-4 py-2 text-blue-600 cursor-pointer hover:underline" onClick={() => handleFinalSelect({ province: activeProvinceObj.name })}>
              Show all in {activeProvinceObj.name}
            </div>
            {activeProvinceObj.districts.map((dist) => (
              <div
                key={dist.name}
                onClick={() => {
                  setActiveDistrict(dist.name);
                  setView("commune");
                }}
                className="px-4 py-2 cursor-pointer hover:bg-muted flex justify-between items-center"
              >
                {dist.name}
                {hasCheck.district(dist.name) && <Check className="w-4 h-4 text-blue-600" />}
              </div>
            ))}
          </div>
        )}

        {/* Commune View */}
        {view === "commune" && activeProvinceObj && activeDistrictObj && (
          <div>
            <div className="flex items-center p-3 bg-gray-100 border-b font-semibold">
              <button
                onClick={() => {
                  setView("district");
                  setActiveDistrict(null);
                }}
                className="mr-2 p-1 hover:bg-gray-200 rounded"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              Location
            </div>
            <div
              className="px-4 py-2 text-blue-600 cursor-pointer hover:underline"
              onClick={() =>
                handleFinalSelect({
                  province: activeProvinceObj.name,
                  district: activeDistrictObj.name,
                })
              }
            >
              Show all in {activeDistrictObj.name}
            </div>
            {activeDistrictObj.communes.map((commune) => (
              <div
                key={commune}
                onClick={() =>
                  handleFinalSelect({
                    province: activeProvinceObj.name,
                    district: activeDistrictObj.name,
                    commune,
                  })
                }
                className="px-4 py-2 cursor-pointer hover:bg-muted flex justify-between items-center"
              >
                {commune}
                {hasCheck.commune(commune) && <Check className="w-4 h-4 text-blue-600" />}
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

